import styled, { css } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';
import { DEVICES } from '_constants';

export const Wrapper = styled.div`
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 10px;
  /* visible so inline cell dropdown menus (e.g. Status) are not clipped */
  overflow: visible;
  background: ${UiColors.fillWhiteColor};
`;

/* The desktop table a PDF export reads on a narrow screen (see DataTable): laid out at a desktop width
   so its columns keep their big-screen proportions, but off-screen and out of reach. Not
   `visibility: hidden` / `display: none` — the reader skips hidden content, and widths need layout. */
export const PdfOffscreen = styled.div`
  position: fixed;
  top: 0;
  left: -20000px;
  width: 1280px;
  pointer-events: none;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-direction: column;

  /* $stickyTop (px): keep the toolbar on screen while the card scrolls — for a media gallery it
     carries the pager and the download/select controls, which are otherwise a screenful away once
     you are into the tiles. It sits here rather than on a single row because a sticky element only
     travels inside its own parent, and this is the child of the card that spans the whole table.
     The offset is what the page's fixed/sticky top bar occupies; the consumer passes it, since the
     ui-kit can't import from modules/. Opaque + rounded so the rows scrolling under it don't show
     through or square off the card's corners. */
  ${({ $stickyTop, $stickyTopCompact }) =>
    $stickyTop != null &&
    css`
      position: sticky;
      top: ${$stickyTop}px;
      /* Over the table's own sticky header (2) and the rows, under the nav rail / top bar (1000+). */
      z-index: 5;
      background: ${UiColors.fillWhiteColor};
      border-radius: 10px 10px 0 0;

      /* The mobile chrome is taller than the desktop top bar (it stacks a search row under it) and
         outranks this row, so the desktop offset would park the toolbar behind it. */
      @media ${DEVICES.tableView} {
        top: ${$stickyTopCompact != null ? $stickyTopCompact : $stickyTop}px;
      }
    `}
`;

export const ToolbarRow = styled.div`
  display: flex;
  /* Top, not centre: the left cluster wraps (view toggle + Select + Tags + search + Advanced Search),
     and centring floated the right-hand actions into the gap between its two lines instead of lining
     them up with the controls they belong beside. Each cell keeps its own centred alignment, so a
     toolbar that fits on one line looks exactly as before. */
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${UiColors.strokeColor};

  /* Tighter spacing on mobile/tablet; wraps when the search + controls can't share one line. */
  @media (max-width: 1023px) {
    gap: 8px;
    padding: 10px 12px;
  }
`;

/* `pinEnd` toolbars (the media galleries): a block pinned to the top-right corner of the row, and
   every other control as ONE group beside it that wraps within whatever width is left.

   The row deliberately does not wrap. Under flex-wrap the pinned block is just another flex item, so
   the moment the controls fill the first line it drops below them — which is how the record/file
   counts ended up under the download buttons instead of staying in the corner. Refusing to wrap is
   what reserves the pinned block's width: it keeps its own size and the controls get the remainder. */
export const ToolbarPinRow = styled.div`
  display: flex;
  /* Top, not centre: the pinned block is three lines tall (two counts + the pager), so centring would
     float the controls beside it into the middle of that block. */
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${UiColors.strokeColor};

  @media (max-width: 1023px) {
    gap: 8px;
    padding: 10px 12px;
  }
`;

/* Everything except the pinned block: one group whose members wrap among themselves. */
export const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  /* min-width: 0 is what lets this shrink below its content width — without it the group keeps its
     full intrinsic size and pushes the pinned block out of the row instead of wrapping inside it. */
  flex: 1 1 auto;
  min-width: 0;

  /* The controls are direct children here (no LeftCluster around them), so this group owns the
     scaffolds' rule that knocks the 40px ui-kit Button down to the 34px toolbar-control height. */
  & > button {
    height: 34px;
  }
`;

/* The pinned block itself — the width the controls have to fit around. */
export const ToolbarPinned = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin-left: auto;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  /* Wraps to a second line rather than overflowing. Buttons carry min-width: max-content and
     flex-shrink: 0 in the new design (a label is never truncated), so without this the cluster
     grew past its own box and painted over whatever sat beside it. */
  flex-wrap: wrap;
  gap: 12px;
  flex: 1 1 320px;
  min-width: 240px;

  @media (max-width: 1023px) {
    flex: 1 1 auto;
    min-width: 0;
    gap: 8px;
  }
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;

  @media (max-width: 1023px) {
    gap: 6px;
    flex: 0 0 auto;
  }
`;

export const MetaLeft = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
`;

export const ExpandAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 8px;
  background: ${UiColors.fillWhiteColor};
  font-size: 13px;
  font-weight: 600;
  color: ${UiColors.forestColor};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

export const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/* Opt-in horizontal scroll (pass `scrollable`): keeps the table from squeezing columns below a
   readable width on narrow desktops — the table grows to its `min-width` and this wrapper scrolls
   instead. Not used by tables with inline cell dropdowns, which rely on the Wrapper's visible
   overflow (overflow-x here would force overflow-y to auto and clip those menus). */
export const TableScroll = styled.div`
  overflow-x: auto;
`;

/* The pinned copy of a scrollable table's header row (see DataTable's cloneHead): sticks with plain
   CSS under the toolbar, and clips sideways — DataTable keeps its scrollLeft on the table's. */
export const StickyHead = styled.div`
  position: sticky;
  top: ${({ $top }) => $top || 0}px;
  z-index: 3;
  overflow: hidden;
  background: ${UiColors.cloudColor};

  /* Its header cells needn't stick inside it. */
  thead th {
    position: static;
  }
`;

/**
 * $roundTop / $roundBottom round the table's outer corners to match the card frame.
 *
 * Driven from props rather than :first-child / :last-child because the table's position in the card
 * depends on runtime state — a toolbar above it, a pagination footer or an empty state below it,
 * and an optional TableScroll wrapper (`scrollable`, off by default) that changes the nesting.
 * Selector guesses about that were silently inert; <DataTable /> knows the answer, so it says so.
 *
 * Clipping the frame instead is not an option: Wrapper keeps `overflow: visible` so the toolbar's
 * column-picker menu is not cut off.
 */
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;

  ${({ $roundTop }) =>
    $roundTop &&
    css`
      thead tr:first-child > th:first-child {
        border-top-left-radius: 10px;
      }

      thead tr:first-child > th:last-child {
        border-top-right-radius: 10px;
      }
    `}

  ${({ $roundBottom }) =>
    $roundBottom &&
    css`
      tbody tr:last-child > td:first-child {
        border-bottom-left-radius: 10px;
      }

      tbody tr:last-child > td:last-child {
        border-bottom-right-radius: 10px;
      }
    `}
  color: ${UiColors.forestColor};

  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: ${minWidth};
    `}

  /* $headTop (px): the bottom edge of a pinned toolbar — the headers stick there instead of at 0. */
  ${({ $headTop }) =>
    $headTop != null &&
    css`
      thead th {
        top: ${$headTop}px;
      }
    `}

  /* $hiddenHead: a scrollable table whose header is drawn by StickyHead above it. The real header row
     stays — it fixes the column widths and names the columns for screen readers — at zero height,
     its contents clipped out of sight (still in the accessibility tree, unlike display: none). */
  ${({ $hiddenHead }) =>
    $hiddenHead &&
    css`
      thead th {
        position: static;
        height: 0;
        padding-top: 0;
        padding-bottom: 0;
        border-bottom: none;
        line-height: 0;
      }

      thead th > * {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
    `}
`;

export const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  text-align: left;
  vertical-align: middle;
  padding: 12px 14px;
  background: ${UiColors.cloudColor};
  border-bottom: 1px solid ${UiColors.strokeColor};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${UiColors.dimGreyFillColor};
  white-space: nowrap;

  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `}

  ${({ center }) =>
    center &&
    css`
      text-align: center;
    `}
`;

/**
 * Row fill lives on the cells rather than on the <tr>.
 *
 * A <tr> background paints as one square rectangle that cannot be rounded — border-radius does not
 * apply to table-row boxes under `border-collapse: collapse` — so it filled the card's rounded
 * corners and made the frame's 1px border look broken there. Per-cell painting lets the end cells
 * of the first and last row round themselves (see Table's $roundTop / $roundBottom).
 */
export const Tr = styled.tr`
  > td {
    background: ${UiColors.fillWhiteColor};
    transition: background 0.12s ease;
  }

  &:hover > td {
    background: ${UiColors.cloudColor};
  }

  ${({ revisit }) =>
    revisit &&
    css`
      > td {
        background: #fdecec;
      }

      &:hover > td {
        background: #fbe0e0;
      }

      /* The marker moves onto the first cell: the cells are opaque now, so an inset shadow on the
         row itself would be painted over. */
      > td:first-child {
        box-shadow: inset 3px 0 0 ${UiColors.errorColor};
      }
    `}
`;

export const Td = styled.td`
  padding: 12px 14px;
  vertical-align: top;
  border-bottom: 1px solid ${UiColors.strokeColor};
  overflow-wrap: anywhere;

  ${({ center }) =>
    center &&
    css`
      text-align: center;
      vertical-align: middle;
    `}

  tr:last-child & {
    border-bottom: none;
  }
`;

export const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: ${UiColors.dimGreyFillColor};
  font-size: 14px;
`;

export const Footer = styled.div`
  border-top: 1px solid ${UiColors.strokeColor};

  /* New-design pagination: round the TableSizeControls BaseSelect dropdowns (rows-per-page /
     page). Lives here so every DataTable-based table gets the consistent footer for free. */
  .table-footer {
    border: none;
    height: auto;
    padding: 10px 16px;
  }

  /* The rounded rows-per-page / page selects now come from ui-kit/TableSizeControls itself, so
     every pagination gets them -- including the ones drawn standalone below a table. */
`;
